import { yupResolver } from '@hookform/resolvers/yup'
import { ELanguage } from '@if/core/types'
import { ControlledCheckbox, ControlledPhoneField, ControlledSelect, ControlledTextField } from '@if/mui'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Box,
  Tooltip,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AsyncActionButton } from '@src/components'
import { useCreateUser, useResetPassword, useUpdateUser } from '@src/data/api/user-api/user-api'
import { MerchantDashboardUser, MerchantDashboardUserSchema } from '@src/data/types/MerchantDashboardUserSchema'
import { AxiosError } from 'axios'
import { memo, useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  handleClose: (user?: MerchantDashboardUser) => void
  user?: MerchantDashboardUser
  merchantId: string
}

const EditProfileDialog = ({ open, handleClose, user, merchantId }: Props) => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, setValue } = useForm<MerchantDashboardUser>({
    mode: 'onBlur',
    defaultValues: { ...user, merchantId },
    resolver: yupResolver(MerchantDashboardUserSchema),
  })

  useEffect(() => {
    reset(user)
  }, [open, reset, user])

  const [editUser, isEditingUser, resetEditUser] = useUpdateUser()
  const [createUser, isCreatingUser, resetCreateUser, createUserError] = useCreateUser()
  const [resetPassword, isResetingPassword, resetResetPassword] = useResetPassword()
  const detailAccessValue = useWatch({
    control,
    name: 'hasDetailAccess',
  })

  useEffect(() => {
    if (!detailAccessValue) {
      setValue('canCreateApplications', false)
    }
  }, [detailAccessValue, setValue])

  const onSubmit = async (data: MerchantDashboardUser) => {
    if (user)
      await editUser({ ...user, ...data }).then((value) => {
        resetEditUser()
        if (value) handleClose(value)
      })
    else
      await createUser(data).then((value) => {
        resetCreateUser()
        if (value) handleClose(value)
      })
  }

  const handleResetPassword = async () => {
    if (user)
      await resetPassword(user.email).then((value) => {
        resetResetPassword()
        if (value) handleClose()
      })
  }
  const isLoading = isEditingUser || isResetingPassword || isCreatingUser

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose()
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      transitionDuration={0}
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title" sx={{ textAlign: 'center' }}>
        {user ? t('profile.edit') : t('profile.addUser')}
        <IconButton
          aria-label="close"
          onClick={() => {
            handleClose()
          }}
          style={{
            position: 'absolute',
            right: 15,
            top: 15,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {createUserError !== null && (
          <Alert sx={{ mt: 1 }} severity="error">
            {(createUserError as AxiosError<string>).response?.data}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              name="firstName"
              control={control}
              label={t('common.firstName')}
              slotProps={{
                htmlInput: { maxLength: 50 },
              }}
              fullWidth
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              name="lastName"
              control={control}
              label={t('common.lastName')}
              slotProps={{
                htmlInput: { maxLength: 50 },
              }}
              fullWidth
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledSelect
              name="language"
              control={control}
              label={t('common.language')}
              disableEmptyOption
              fullWidth
            >
              <MenuItem value="en">{t(`ELanguage.${ELanguage.English}`, { ns: 'enum' })}</MenuItem>
              <MenuItem value="fr">{t(`ELanguage.${ELanguage.French}`, { ns: 'enum' })}</MenuItem>
            </ControlledSelect>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              name="jobTitle"
              control={control}
              label={t('common.jobTitle')}
              slotProps={{
                htmlInput: { maxLength: 50 },
              }}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledPhoneField name="phone" control={control} label={t('common.phone')} fullWidth />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              name="email"
              control={control}
              type="email"
              label={t('common.email')}
              disabled={!!user}
              slotProps={{
                htmlInput: { maxLength: 50 },
              }}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 7,
            }}
          >
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 4,
                }}
              >
                <Stack spacing={2}>
                  <ControlledCheckbox
                    control={control}
                    name="isDeveloper"
                    label={t('profile.developer')}
                    labelPlacement="end"
                    controlProps={{
                      id: 'isDeveloper',
                    }}
                  />
                  <ControlledCheckbox
                    control={control}
                    name="accountEnabled"
                    label={t('profile.active')}
                    labelPlacement="end"
                    disabled={!user}
                    controlProps={{
                      id: 'accountEnabled',
                    }}
                  />
                </Stack>
              </Grid>
                  <Grid
                    size={{
                      xs: 4,
                    }}
                  >
                    <Stack spacing={2}>
                      <ControlledCheckbox
                        control={control}
                        name="hasDetailAccess"
                        label={
                          <Box display="flex" alignItems="center">
                            {t('profile.detailAccess')}
                            <Tooltip title={t('profile.detailAccessInfo')}>
                              <InfoOutlinedIcon color="action" fontSize="small" sx={{ cursor: 'pointer', ml: 0.5 }} />
                            </Tooltip>
                          </Box>
                        }
                        labelPlacement="end"
                        controlProps={{
                          id: 'hasDetailAccess',
                        }}
                      />
                      <ControlledCheckbox
                        control={control}
                        name="canCreateApplications"
                        label={
                          <Box display="flex" alignItems="center">
                            {t('profile.createApplications')}
                            <Tooltip title={t('profile.createApplicationsInfo')}>
                              <InfoOutlinedIcon color="action" fontSize="small" sx={{ cursor: 'pointer', ml: 0.5 }} />
                            </Tooltip>
                          </Box>
                        }
                        labelPlacement="end"
                        disabled={!detailAccessValue}
                        controlProps={{
                          id: 'canCreateApplications',
                        }}
                      />
                    </Stack>
                  </Grid>
                  <Grid
                    size={{
                      xs: 4,
                    }}
                  >
                    <Stack spacing={2}>
                      <ControlledCheckbox
                        control={control}
                        name="isAdmin"
                        label={
                          <Box display="flex" alignItems="center">
                            {t('profile.adminAccess')}
                            <Tooltip title={t('profile.adminAccessInfo')}>
                              <InfoOutlinedIcon color="action" fontSize="small" sx={{ cursor: 'pointer', ml: 0.5 }} />
                            </Tooltip>
                          </Box>
                        }
                        controlProps={{
                          id: 'isAdmin',
                        }}
                      />
                      <ControlledCheckbox
                        control={control}
                        name="canSendWelcomeMessage"
                        label={
                          <Box display="flex" alignItems="center">
                            {t('profile.welcomeMessage')}
                            <Tooltip title={t('profile.welcomeMessageInfo')}>
                              <InfoOutlinedIcon color="action" fontSize="small" sx={{ cursor: 'pointer', ml: 0.5 }} />
                            </Tooltip>
                          </Box>
                        }
                        controlProps={{
                          id: 'canSendWelcomeMessage',
                        }}
                      />
                    </Stack>
                  </Grid>
            </Grid>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 5,
            }}
          >
            <Stack direction="row" spacing={5}>
              <AsyncActionButton
                variant="outlined"
                onClick={handleResetPassword}
                isPending={isResetingPassword}
                disabled={isLoading || user === undefined}
              >
                {t('profile.resetPassword')}
              </AsyncActionButton>
              <AsyncActionButton
                variant="outlined"
                isPending={isEditingUser || isCreatingUser}
                disabled={isLoading}
                onClick={async () => {
                  await handleSubmit(onSubmit)()
                }}
              >
                {user ? t('profile.save') : t('profile.addUser')}
              </AsyncActionButton>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default memo(EditProfileDialog)
